// components/YourComponentPath/RoundButton.tsx

import React, { FC } from "react";
import { twMerge } from "tailwind-merge";
import { useStore } from "../../store/store";
import { filterHoverClasses } from "../../utils/css";

interface CloseArtworkButtonProps {
  text?: string;
  icon: string;
  onClick: () => void;
  buttonClassName?: string;
  disabled?: boolean; // Added disabled prop
}

const RoundButton: FC<CloseArtworkButtonProps> = ({
  text,
  icon,
  onClick,
  buttonClassName = "",
  disabled = false, // Default to false
}) => {
  const { audioManager } = useStore();

  // Base button classes
  const baseButtonClasses =
    "button select-none flex justify-center items-center p-2 border-2 rounded-full text-white border-white/50 transition backdrop-blur";

  // Conditional classes based on disabled state
  const conditionalClasses = disabled
    ? "cursor-not-allowed opacity-50"
    : "cursor-pointer hover:border-white/100";

  // Filter out hover classes from buttonClassName if disabled
  const processedButtonClassName = disabled
    ? filterHoverClasses(buttonClassName)
    : buttonClassName;

  // Merge all classes
  const buttonClasses = twMerge(
    baseButtonClasses,
    conditionalClasses,
    processedButtonClassName,
  );

  // Click event handler
  const handleClick = (event: React.MouseEvent) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    audioManager?.play("click");
    onClick();
  };

  return (
    <div className="relative flex items-center gap-3">
      <button
        type="button"
        className={buttonClasses}
        onClick={handleClick}
        disabled={disabled} // Adds the disabled attribute to the button
        aria-disabled={disabled} // Accessibility attribute
        // Optionally, remove the button from the tab order when disabled
        {...(disabled && { tabIndex: -1 })}
      >
        <span className={`d-icon ${icon} text-base flex items-center`}></span>
      </button>
      {text && <div className="hidden sm:block text-white">{text}</div>}
    </div>
  );
};

export default RoundButton;
